import { Plus, Search, Upload } from 'lucide-react'
import { useCallback } from 'react'

import Flex from '@/elements/Flex'
import FormSelect from '@/elements/FormSelect'
import FormInput from '@/elements/FormInput'
import Button from '@/elements/Button'
import UploadModal from '@/components/UploadModal'
import useBreakpoints from '@/hooks/useBreakpoints'
import useRoles from '@/hooks/useRoles'
import type { UploadErrorsType } from '@/hooks/useUploadErrors'
import { OrganizationMemberRolesWithoutOrgLead, type OrganizationMemberRolesType } from '@/api/types/Organization'
import { uploadOrganizationMembersCSV } from '@/api/organizationMember'
import { MEMBERS_CSV_TEMPLATE } from '@/constants'
import toast from '@/utils/toast'

interface OrganizationMembersFilterBarProps {
  filterByName: string
  setFilterByName: (name: string) => void
  filterByRole: string
  setFilterByRole: (role: string) => void
  refetchMembers: () => void
  handleOpenAddUserModal?: () => void
}

function OrganizationMembersFilterBar({
  filterByName,
  setFilterByName,
  filterByRole,
  setFilterByRole,
  refetchMembers,
  handleOpenAddUserModal,
}: OrganizationMembersFilterBarProps) {
  const { isMobile, isTablet } = useBreakpoints()
  const { getRoleName } = useRoles()

  const handleUploadMembersCSV = useCallback(async (files: File[]) => {
    if (files.length === 0) return null

    const formData = new FormData()
    formData.append('csv', files[0])

    try {
      await uploadOrganizationMembersCSV(formData)
      toast.success('Members CSV uploaded successfully!')
      refetchMembers()
      return null
    } catch (error: any) {
      const responseError = error.errors as UploadErrorsType
      toast.error(error.message || 'Failed to upload Members CSV.')
      return responseError
    }
  }, [refetchMembers])

  return (
    <Flex inline wfull justify='space-between' align='end' gh={4} gv={3} wrap>
      <Flex inline={!isMobile} grow={!isMobile} align='end' gap={3} gv={2} wfull={isTablet} wrap={isMobile}>
        <FormSelect<OrganizationMemberRolesType>
          isMulti
          name='role-filter'
          placeholder='All Roles'
          options={OrganizationMemberRolesWithoutOrgLead}
          values={Array.from(OrganizationMemberRolesWithoutOrgLead).filter(role => filterByRole.split(',').includes(role))}
          onChange={roles => setFilterByRole(roles.join(','))}
          optionLabel={item => getRoleName(item)}
          optionValue={item => item}
          style={{ width: isMobile ? '100%' : 148 }}
        />
        <FormInput
          name='search-filter'
          placeholder='Search members...'
          Icon={Search}
          style={{ width: '100%' }}
          value={filterByName}
          onChangeDebounced={val => setFilterByName(val)}
        />
      </Flex>
      <Flex inline align='end' grow gap={3} gv={2} wfull={isTablet} wrap={isMobile}>
        <UploadModal
          title='Upload Members CSV'
          description='Select CSV file to upload'
          accept={{ 'text/csv': ['.csv'] }}
          onUpload={handleUploadMembersCSV}
          sampleFileUrl={MEMBERS_CSV_TEMPLATE}
        >
          <Button fullWidth>
            <Upload size={18} style={{ minWidth: 18 }} />
            Upload CSV
          </Button>
        </UploadModal>
        <Button variant='primary' fullWidth onClick={handleOpenAddUserModal}>
          <Plus size={18} />
          Add Member
        </Button>
      </Flex>
    </Flex>
  )
}

export default OrganizationMembersFilterBar
